import { useCallback, useMemo } from 'react';

import { useCheckAnswer } from 'approot/activity-builder/check-answer/check-answer.hooks';
import { setComponentMultiAnswer } from 'approot/activity-builder/check-answer/check-answer.utils';
import {
  canShowOptionLetter,
  getOptionsPerColumn,
  parseMultipleChoiceTypes,
  shouldDisableUnselected,
  sortOptions,
} from './multiple-choice.utils';
import { isOptionCorrect, isOptionIncorrect } from './logic/get-option-status';
import MultipleChoiceViewList from './list/multiple-choice-view-list';
import { MultipleChoiceItems } from './multiple-choice.style';
import {
  MultipleChoiceButton,
  MultipleChoiceButtonText,
  MultipleChoiceContent,
  MultipleChoiceTextContent,
  MultipleChoiceViewImage,
} from './multiple-choice-view.style';
import {
  MultipleChoiceLetter,
  MultipleChoiceLetterImage,
  MultipleChoiceStatus,
} from './multiple-choice';
import MultipleChoiceTextImageView from './text-image/multiple-choice-text-image-view';
import MultipleChoiceImage from './image/multiple-choice-image';
import { ANSWER_LABELS, ViewProps } from './multiple-choice.constants';
import { isSingleSelect } from './logic/is-single-select';

type Props = ViewProps & {
  isResponsive: boolean;
  width?: number;
  showAnswer: boolean;
  onSelectOption?: (answerRefs: string[]) => void;
};

const MultipleChoiceViewOptions = (props: Props) => {
  const {
    properties,
    sectionId,
    id,
    isResponsive,
    width,
    onSelectOption,
    showAnswer,
  } = props;

  const { checkAnswerData, hasAnswered } = useCheckAnswer({
    componentId: id,
  });
  const componentAnswers = checkAnswerData[id];

  const {
    multipleChoice: {
      options,
      answerMode,
      isFreeAnswerMultiSelect,
      optionMinHeight,
      showOptionLetter,
      imagePosition,
    },
  } = properties;

  const {
    isList,
    isText,
    isImage,
    isTextAndImage,
    isFree: isFreeAnswer,
  } = parseMultipleChoiceTypes(properties);

  const canShowCorrectOrIncorrect = showAnswer === true && !isFreeAnswer;

  const selectedAnswers = useMemo(
    () => componentAnswers?.multiAnswer?.answerRefs || [],
    [componentAnswers]
  );

  const sortedOptions = useMemo(() => sortOptions(options), [options]);

  const isOneOptionCorrect = useMemo(
    () =>
      isSingleSelect({
        answerMode,
        isFreeAnswerMultiSelect,
        options,
      }),
    [options, answerMode, isFreeAnswerMultiSelect]
  );

  const shouldDisableUnselectedOptions = useMemo(
    () =>
      shouldDisableUnselected({
        options,
        selectedAnswers,
        isOneOptionCorrect,
        isFreeAnswer,
      }),
    [options, selectedAnswers, isOneOptionCorrect, isFreeAnswer]
  );

  const showIsCorrect = useCallback(
    (showCorrect: boolean) => {
      return showCorrect && canShowCorrectOrIncorrect;
    },
    [canShowCorrectOrIncorrect]
  );

  const showInCorrect = useCallback(
    (showIncorrect: boolean) => {
      return showIncorrect && canShowCorrectOrIncorrect;
    },
    [canShowCorrectOrIncorrect]
  );

  const showSelected = useMemo(() => {
    return hasAnswered;
  }, [hasAnswered]);

  return (
    <>
      {isList && isText ? (
        <MultipleChoiceViewList {...props} />
      ) : (
        <MultipleChoiceItems
          role={isOneOptionCorrect ? 'radiogroup' : 'group'}
          $display={properties.layout?.display}
          $isResponsive={isResponsive}
          style={{ maxWidth: width ? `${width}px` : undefined }}
          data-selector="multi-choice-options-group"
        >
          {sortedOptions.map((option, index) => {
            const optionSelected = selectedAnswers.includes(
              option.itemId || '1'
            );
            const showCorrect =
              isOptionCorrect({
                showAnswer: canShowCorrectOrIncorrect,
                isOptionSelected: optionSelected,
                hasAnswered,
                option,
              }) ?? false;
            const showIncorrect =
              isOptionIncorrect({
                showAnswer: canShowCorrectOrIncorrect,
                isOptionSelected: optionSelected,
                hasAnswered,
                option,
              }) ?? false;

            const isDisabled = isOneOptionCorrect
              ? hasAnswered && !optionSelected
              : !optionSelected &&
                (hasAnswered || shouldDisableUnselectedOptions);

            return (
              <MultipleChoiceButton
                data-selector="multi-choice-option-pill"
                key={index}
                type="button"
                role={isOneOptionCorrect ? 'radio' : 'checkbox'}
                disabled={isDisabled}
                onClick={() => {
                  let newAnswerRefs = selectedAnswers;
                  if (option.itemId && !hasAnswered) {
                    newAnswerRefs = isOneOptionCorrect
                      ? [option.itemId]
                      : optionSelected
                        ? selectedAnswers.filter(id => id !== option.itemId)
                        : [...selectedAnswers, option.itemId];
                    setComponentMultiAnswer({
                      sectionId,
                      componentId: id,
                      answers: newAnswerRefs,
                      checkAnswerData,
                    });
                  }

                  onSelectOption?.(newAnswerRefs);
                }}
                $isText={isText}
                $itemsPerRow={getOptionsPerColumn(properties.multipleChoice)}
                $isActive={optionSelected}
                $isCorrect={showIsCorrect(showCorrect)}
                $isIncorrect={showInCorrect(showIncorrect)}
                $isSelected={showSelected}
                $isDisabled={isDisabled}
                $minHeight={optionMinHeight || 80}
                aria-checked={optionSelected}
                aria-label={[
                  `Multiple choice option ${ANSWER_LABELS[index]}${
                    isText || isTextAndImage
                      ? `: ${option.text}`
                      : `${option.image?.alt || ''}`
                  }.`,
                  hasAnswered && showCorrect ? ' This option is correct' : '',
                  hasAnswered && showIncorrect
                    ? ' This option is incorrect'
                    : '',
                ].join('')}
              >
                {canShowCorrectOrIncorrect && (
                  <MultipleChoiceStatus
                    isCorrect={hasAnswered && showCorrect ? true : undefined}
                    isIncorrect={
                      hasAnswered && showIncorrect ? true : undefined
                    }
                  />
                )}

                {isTextAndImage ? (
                  <MultipleChoiceTextImageView
                    optionMinHeight={optionMinHeight}
                    imagePosition={imagePosition}
                    showOptionLetter={showOptionLetter}
                    option={option}
                    index={index}
                  />
                ) : isImage ? (
                  <MultipleChoiceViewImage>
                    <MultipleChoiceImage option={option} />
                    {canShowOptionLetter(showOptionLetter) && (
                      <MultipleChoiceLetterImage index={index} />
                    )}
                  </MultipleChoiceViewImage>
                ) : (
                  <MultipleChoiceContent>
                    <MultipleChoiceTextContent>
                      {canShowOptionLetter(showOptionLetter) && (
                        <MultipleChoiceLetter index={index} />
                      )}
                      <MultipleChoiceButtonText>
                        {option.text}
                      </MultipleChoiceButtonText>
                    </MultipleChoiceTextContent>
                  </MultipleChoiceContent>
                )}
              </MultipleChoiceButton>
            );
          })}
        </MultipleChoiceItems>
      )}
    </>
  );
};

export default MultipleChoiceViewOptions;
